import styled, { css } from 'styled-components'
import {
  getDailyStandardPoolRewardsForStake,
  getTokenMultiplier,
  improvedAmountFromWei,
  UserTokenStake,
  UserVaultStake,
  useStakingContext,
} from '../../../contexts/StakingContext'
import useNetworkId from '../../../hooks/useNetworkId'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import SwitchToBaseButton from '../../../shared_components/Library/Buttons/SwitchToBaseButton'
import {
  H2,
  H3,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import { Box } from '../../../shared_components/UI/Box'
import { Divider, DotsButton, ProgressBar, Title, TokenAmount } from './styles'
import routes from '../../../router/routes'
import { Link, useHistory } from 'react-router-dom'
import { daysRemaining, daysSince, daysUntil, tierMonthsMap } from './helpers'
import FolderLock from '../../../shared_components/Icons/FolderLock'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import { Dialog, DialogContent } from '../../../shared_components/Digalog'
import { BackButton, PureBackButton } from '../../../shared_components/Page'
import { useShouldShowForThreshold } from '../../../theme'
import { useWindowSize } from '../../../hooks/useWindowSize'
import { CheckoutModalOverlay } from '../../Staking/StakingCheckout'
import { Menu, MenuItem } from '../../../shared_components/Library/DropdownMenu'
import XCircle from '../../../shared_components/Icons/XCircle'

function TokenAndVaults() {
  const { wrongNetwork } = useNetworkId()
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  const {
    vaultsAvailable,
    vaultsHeld,
    vaultsClaimed,
    vaultsStaked,
    userVaultStakes,
    isLoadingUserVaultsState,
    stakedVaultsPercentage,
    userTokenStakes,
    tokensAvailable,
    tokensStaked,
    stakedTokensPercentage,
    tokensClaimableFromRewards,
    rewardsClaimStatus,
    vestClaimedVaultsStatus,
    tokensClaimableFromVesting,
    vestClaimedVaults,
    claimRewards,
    resetStatusByKind,
    pendingSablierBalances,
    claimSablierStream,
    sablierClaimStatus,
    totalBonusPoolTokensEmittedFromVaults,
    totalStandardPoolTokensEmittedFromVaults,
  } = useStakingContext()

  const hasEmmissions =
    totalBonusPoolTokensEmittedFromVaults > 0n ||
    totalStandardPoolTokensEmittedFromVaults > 0n

  const dismissModal = useCallback(() => {
    resetStatusByKind('rewards-claimed')
    resetStatusByKind('vest-claimed')
    resetStatusByKind('sablier-claimed')
  }, [resetStatusByKind])

  const { push } = useHistory()
  const redirectAndDismiss = () => {
    push(routes.staking.tokens)
    dismissModal()
  }

  useEffect(() => {
    return () => {
      dismissModal()
    }
  }, [dismissModal])

  const claimStatuses = {
    'vest-claimed': {
      ...vestClaimedVaultsStatus,
      label: 'vault emissions',
    },
    'rewards-claimed': {
      ...rewardsClaimStatus,
      label: 'staking rewards',
    },
    [sablierClaimStatus.streamId]: sablierClaimStatus,
  }
  const successClaim = Object.values(claimStatuses).find(
    (o) => o.status === 'success',
  )
  const totalPendingSablierBalances = pendingSablierBalances?.reduce(
    (acc, stream) => acc + improvedAmountFromWei(stream.balance),
    0n,
  )
  const [isInvestorClaiming, setIsInvestorClaiming] = useState(false)
  const onInvestorClaim = async () => {
    try {
      setIsInvestorClaiming(true)
      await Promise.all(
        pendingSablierBalances?.map((stream) => claimSablierStream(stream)),
      )
    } catch (e) {
      console.error('Error claiming stream', e)
    } finally {
      setIsInvestorClaiming(false)
    }
  }

  if (isLoadingUserVaultsState) {
    return (
      <Box c ac>
        <AnimatedLoader onlyWhite size={32} />
      </Box>
    )
  }

  const vaultStakesGroupedByTierIndex = userVaultStakes?.reduce(
    (acc, obj) => {
      if (!acc[obj.tierIndex]) {
        acc[obj.tierIndex] = []
      }
      acc[obj.tierIndex].push(obj)
      return acc
    },
    {} as { [key: number]: UserVaultStake[] },
  )

  const hasClaimedVaults = Number(vaultsClaimed) > 0
  const hasStakedVaults = Number(vaultsStaked) > 0
  const isMaxVaultStake = stakedVaultsPercentage === 100

  const tokensStakesGroupedByTierIndex = userTokenStakes?.reduce(
    (acc, obj) => {
      if (!acc[obj.tierIndex]) {
        acc[obj.tierIndex] = []
      }
      acc[obj.tierIndex].push(obj)
      return acc
    },
    {} as { [key: number]: UserTokenStake[] },
  )

  const hasStakedTokens = Number(tokensStaked) > 0
  const isMaxTokenStake = stakedTokensPercentage === 100

  return (
    <Box dc g="var(--units-32, 32px)">
      <Title>Tokens & Vaults</Title>
      {wrongNetwork ? (
        <SwitchToBaseButton />
      ) : (
        <>
          <Box dc g="var(--units-32, 32px)">
            {vaultsHeld > 0 && (
              <OverviewBox>
                <Box dc g="var(--units-16, 16px)">
                  <Box g={4} ac>
                    <img
                      style={{ height: '24px' }}
                      src="/vault-icon.svg"
                      alt="zap vault icon"
                    />
                    <P s="XL" fw="semibold">
                      Vaults Overview
                    </P>
                  </Box>

                  <Box dc g={8}>
                    <Box>
                      <ProgressBar
                        w={Number(stakedVaultsPercentage)}
                        c={
                          isMaxVaultStake
                            ? 'var(--colour-function-success, #39FF41)'
                            : 'var(--surface-brand, #FF0)'
                        }
                      />
                    </Box>
                  </Box>
                  <Box g={16}>
                    <StatsCard
                      target="vaults"
                      targetType="staked"
                      value={vaultsStaked}
                      label="Staked"
                      stroke={isMaxVaultStake ? 'success' : 'brand'}
                    />
                    <StatsCard
                      target="vaults"
                      targetType="claimed"
                      value={vaultsClaimed}
                      label="Claimed"
                      stroke="primary"
                    />
                    <StatsCard
                      target="vaults"
                      targetType="unclaimed"
                      value={vaultsAvailable}
                      label="Unclaimed"
                      stroke="tertiary"
                    />
                  </Box>
                </Box>
              </OverviewBox>
            )}
            {hasEmmissions && (
              <OverviewBox>
                <Box dc g="var(--units-16, 16px)">
                  <Box g={4} ac>
                    <img
                      style={{ height: '24px' }}
                      src="/token-icon.svg"
                      alt="zap token icon"
                    />
                    <P s="XL" fw="semibold">
                      Emitted from vaults
                    </P>
                  </Box>
                  <Box g={16}>
                    <StatsCard
                      target="vaults"
                      targetType="staked"
                      value={improvedAmountFromWei(
                        totalBonusPoolTokensEmittedFromVaults,
                      )}
                      label="Bonus Pool"
                      stroke="brand"
                    />
                    <StatsCard
                      target="vaults"
                      targetType="claimed"
                      value={improvedAmountFromWei(
                        totalStandardPoolTokensEmittedFromVaults,
                      )}
                      label="Standard Pool"
                      stroke="primary"
                    />
                  </Box>
                </Box>
              </OverviewBox>
            )}
            <OverviewBox>
              <Box dc g="var(--units-16, 16px)">
                <Box g={4} ac>
                  <img
                    style={{ height: '24px' }}
                    src="/token-icon.svg"
                    alt="zap token icon"
                  />
                  <P s="XL" fw="semibold">
                    Tokens Overview
                  </P>
                </Box>
                <Box dc g={8}>
                  <Box>
                    <ProgressBar
                      w={Number(stakedTokensPercentage)}
                      c={
                        isMaxTokenStake
                          ? 'var(--colour-function-success, #39FF41)'
                          : 'var(--surface-brand, #FF0)'
                      }
                    />
                  </Box>
                </Box>
                <Box g={16}>
                  <StatsCard
                    target="tokens"
                    targetType="staked"
                    value={tokensStaked.toLocaleString()}
                    label="Staked"
                    stroke={isMaxTokenStake ? 'success' : 'brand'}
                  />
                  <StatsCard
                    target="tokens"
                    targetType="available"
                    value={tokensAvailable.toLocaleString()}
                    label="Available"
                    stroke="tertiary"
                  />
                  {totalPendingSablierBalances > 0 && (
                    <StatsCard
                      target="tokens"
                      targetType="unclaimed"
                      value={totalPendingSablierBalances.toLocaleString()}
                      onInvestorClaim={onInvestorClaim}
                      disabled={isInvestorClaiming}
                      label="Unclaimed"
                      stroke="tertiary"
                    />
                  )}
                </Box>
              </Box>
            </OverviewBox>

            {hasClaimedVaults && (
              <StyledVaultsBox dc g={12} pad={16}>
                <Box ac g={8}>
                  <Box>
                    <Strong s="XL" fw="semibold" c="text-primary">
                      Claimed Vaults
                    </Strong>
                  </Box>
                  <Box jfe mw="138px">
                    <Span s="M" fw="regular" c="text-secondary">
                      Vault vesting ends
                    </Span>
                  </Box>
                  {/*<Box jfe mw="100px">
                  <Span s="M" fw="regular" c="text-secondary">
                    Emissions
                  </Span>
                </Box>*/}
                </Box>
                <Divider />
                <Box dc g={24} padV={16}>
                  <ClaimedVaultsRow />
                </Box>
                <Divider />
                <Box jsb padT={12}>
                  <ClaimButton
                    isLoading={vestClaimedVaultsStatus.status === 'vesting'}
                    action={() => vestClaimedVaults(tokensClaimableFromVesting)}
                    disabled={tokensClaimableFromVesting <= 0n}
                    label="Claim tokens"
                  />
                  <TokenAmount
                    amount={tokensClaimableFromVesting}
                    label="Total emissions"
                  />
                </Box>
              </StyledVaultsBox>
            )}

            {(hasStakedVaults || hasStakedTokens) && (
              <StyledVaultsBox dc g={12} pad={16}>
                {hasStakedVaults && (
                  <>
                    <Box ac g={8}>
                      <Box>
                        <Strong s="XL" fw="semibold" c="text-primary">
                          Staked Vaults
                        </Strong>
                      </Box>
                      <Box jfe mw="88px">
                        {/* Auto-relock column */}
                      </Box>
                      <Box jfe mw="60px">
                        <Span s="M" fw="regular" c="text-secondary">
                          Remaining
                        </Span>
                      </Box>
                    </Box>
                    <Divider />
                    <Box dc g={24} padT={16}>
                      {Object.entries(vaultStakesGroupedByTierIndex)
                        .sort(
                          ([tierIndexA], [tierIndexB]) =>
                            Number(tierIndexB) - Number(tierIndexA),
                        )
                        .map(([tierIndex, stakedVaults]) => {
                          return stakedVaults.map((stakedVault, index) => (
                            <>
                              <VaultsRow
                                key={`vaults-${tierIndex}-${stakedVault.vaultsStaked}`}
                                {...stakedVault}
                              />
                              <Divider />
                            </>
                          ))
                        })}
                    </Box>
                  </>
                )}
                {hasStakedTokens && (
                  <>
                    <Box padV={4}>
                      <Strong s="XL" fw="semibold" c="text-primary">
                        Staked Tokens
                      </Strong>
                    </Box>
                    <Divider />
                    <Box dc g={24} padT={16}>
                      {Object.entries(tokensStakesGroupedByTierIndex)
                        .sort(
                          ([tierIndexA], [tierIndexB]) =>
                            Number(tierIndexB) - Number(tierIndexA),
                        )
                        .map(([tierIndex, stakedTokens]) => {
                          return stakedTokens.map((stakedToken, index) => (
                            <>
                              <TokensRow
                                key={`vaults-${tierIndex}-${stakedToken.tokensStaked}`}
                                {...stakedToken}
                              />
                              <Divider />
                            </>
                          ))
                        })}
                    </Box>
                  </>
                )}
                <Box jsb padT={12}>
                  <ClaimButton
                    isLoading={rewardsClaimStatus.status === 'claiming'}
                    action={() => claimRewards(tokensClaimableFromRewards)}
                    disabled={tokensClaimableFromRewards <= 0n}
                    label="Claim staking rewards"
                  />
                  <div
                    style={{
                      opacity:
                        Number(tokensClaimableFromRewards) <= 0 ? 0.5 : 1,
                    }}
                  >
                    <TokenAmount
                      amount={tokensClaimableFromRewards}
                      label="Total rewards"
                    />
                  </div>
                </Box>
              </StyledVaultsBox>
            )}

            {/*{pendingSablierBalances?.map((stream, index) => (
              <ClaimBox key={index} dc g={16} pad={16}>
                <P s="L" fw="regular">
                  {stream.label}
                </P>
                <Divider />
                <Box>
                  <TokenAmount
                    amount={Number(
                      improvedAmountFromWei(stream.balance),
                    ).toLocaleString()}
                  />
                  <ClaimButton
                    isLoading={
                      sablierClaimStatus.streamId === stream.streamId &&
                      sablierClaimStatus.status === 'claiming'
                    }
                    action={() => claimSablierStream(stream)}
                  />
                </Box>
              </ClaimBox>
            ))}*/}
          </Box>

          <ModalOverlay isOpen={!!successClaim} dismiss={dismissModal}>
            {successClaim?.status === 'success' && (
              <OuterBox>
                <InnerBox dc g={32} padV={24} padH={24} c>
                  <img src="/claim-image.png" alt="claim success" />

                  <Box dc g={12} c>
                    <H2
                      s="2XL"
                      fw="bolder"
                      c="text-brandPrimary"
                      uc
                      ff="Proxima Nova"
                    >
                      {successClaim?.amount.toLocaleString()}
                    </H2>
                    <Strong s="M" fw="semibold" ls="-0.28px" c="text-primary">
                      From {successClaim?.label}
                    </Strong>
                  </Box>

                  <Box dc g={12} c>
                    <ThemedButton
                      onClick={redirectAndDismiss}
                      $styleType="primary"
                    >
                      Stake tokens
                    </ThemedButton>

                    <Box dc g={12} c padV={12}>
                      <P s="L" fw="semibold" c="text-primary" tac>
                        Want to trade your tokens?
                        <br />
                        <a
                          onClick={dismissModal}
                          href="https://www.bybit.com/user/assets/deposit"
                          target="_blank"
                          rel="noreferrer"
                          style={{ paddingTop: '8px', display: 'block' }}
                        >
                          <Strong
                            s="L"
                            fw="semibold"
                            ls="-0.28px"
                            c="text-brandPrimary"
                          >
                            Deposit on Bybit
                          </Strong>
                        </a>
                      </P>
                    </Box>
                  </Box>
                </InnerBox>
              </OuterBox>
            )}
          </ModalOverlay>
        </>
      )}
    </Box>
  )
}

export default TokenAndVaults

const OverviewBox = styled(Box)`
  padding: var(--units-16, 16px);
  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`

type StatsStroke = 'brand' | 'primary' | 'tertiary' | 'secondary' | 'success'
const StatsStrokeBorderColor = {
  brand: 'var(--stroke-brand, #FF0)',
  primary: 'var(--stroke-primary, #FFF)',
  tertiary: 'var(--stroke-tertiary, #252525)',
  secondary: 'var(--stroke-secondary, #B1B1B1)',
  success: 'var(--stroke-success, #39FF41)',
}

const StatsContainer = styled(Box)<{ stroke: StatsStroke; bordered?: boolean }>`
  min-height: 78px;
  border-radius: var(--units-8, 8px);
  ${({ stroke, bordered }) =>
    bordered
      ? css`
          border: var(--units-1, 1px) solid ${StatsStrokeBorderColor[stroke]};
        `
      : css`
          border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
        `}
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`

export const StatsCard = ({
  target,
  targetType,
  value,
  label,
  onInvestorClaim,
  disabled,
  stroke = 'primary',
  bordered = true,
}: {
  target: 'vaults' | 'tokens'
  targetType: 'staked' | 'claimed' | 'available' | 'unclaimed'
  value: string | number | bigint
  label: string
  onInvestorClaim?: () => void
  disabled?: boolean
  stroke: StatsStroke
  bordered?: boolean
}) => (
  <StatsContainer padH={16} dc jc afs stroke={stroke} bordered={bordered}>
    <H2
      s="L"
      fw="bolder"
      uc
      c={
        stroke === 'brand'
          ? 'text-brandPrimary'
          : stroke === 'primary'
            ? 'text-primary'
            : stroke === 'success'
              ? 'function-success'
              : 'text-secondary'
      }
      lh={12}
      ff="Proxima Nova"
    >
      {value.toString()}
    </H2>
    <P
      s="S"
      c={targetType === 'unclaimed' ? 'text-secondary' : 'text-primary'}
      fw="regular"
      lh={10}
      padT={12}
    >
      {label}
    </P>
    {target === 'vaults' &&
      targetType === 'unclaimed' &&
      String(value) !== '0' && (
        <Link to={routes.staking.vaults}>
          <P s="S" c="text-brandPrimary" fw="regular" lh={10} tdu padT={6}>
            Stake or claim
          </P>
        </Link>
      )}
    {target === 'tokens' &&
      targetType === 'available' &&
      String(value) !== '0' && (
        <Link to={routes.staking.tokens}>
          <P s="S" c="text-brandPrimary" fw="regular" lh={10} tdu padT={6}>
            Stake
          </P>
        </Link>
      )}
    {target === 'tokens' &&
      targetType === 'unclaimed' &&
      String(value) !== '0' && (
        <button onClick={onInvestorClaim} disabled={disabled}>
          <P s="S" c="text-brandPrimary" fw="regular" lh={10} tdu padT={6}>
            Claim
          </P>
        </button>
      )}
  </StatsContainer>
)

export function VaultsRow({
  tierIndex,
  vaultsStaked,
  previousStake,
  stakedAt,
  expiresAt,
  unstakedAt,
  autoRelock,
}: UserVaultStake) {
  const {
    getEarlyUnstakeFee,
    getEstimatedTokensPerVaultOverStake,
    unstakeVaults,
    vaultsUnstakingStatus,
    totalTokensStaked,
    calculateDailyBonusPoolTokensForVault,
  } = useStakingContext()
  const _vaultsStaked = Number(vaultsStaked)

  const _expiresAt = new Date(Number(expiresAt * 1000n))
  const daysRemainingInStake = daysRemaining(_expiresAt)

  const _stakedAt = new Date(Number(stakedAt * 1000n))
  const daysSinceStake = daysSince(_stakedAt)

  const estimatedTokensPerVault = getEstimatedTokensPerVaultOverStake(tierIndex)
  const estimatedTokensForVaultsStaked = Number(
    improvedAmountFromWei(estimatedTokensPerVault * BigInt(_vaultsStaked)),
  )
  const standardPoolRewardsForStake = getDailyStandardPoolRewardsForStake(
    Number(estimatedTokensForVaultsStaked),
    1,
    totalTokensStaked,
  )

  const bonusPoolRewardsForStake =
    Number(
      improvedAmountFromWei(calculateDailyBonusPoolTokensForVault(tierIndex)),
    ) * _vaultsStaked
  const totalRewardsLeftToGain =
    standardPoolRewardsForStake * daysRemainingInStake +
    bonusPoolRewardsForStake * daysRemainingInStake

  const [earlyUnstakeFee, setEarlyUnstakeFee] = useState<bigint>(0n)
  useEffect(() => {
    if (!getEarlyUnstakeFee || !vaultsStaked || !stakedAt || !expiresAt) return

    getEarlyUnstakeFee(
      BigInt(estimatedTokensForVaultsStaked),
      Number(stakedAt),
      Number(expiresAt),
    ).then((fee) => setEarlyUnstakeFee(improvedAmountFromWei(fee)))
  }, [
    getEarlyUnstakeFee,
    vaultsStaked,
    stakedAt,
    expiresAt,
    estimatedTokensForVaultsStaked,
  ])

  const totalRewardsSinceStake =
    standardPoolRewardsForStake * daysSinceStake +
    bonusPoolRewardsForStake * daysSinceStake

  const estimatedTokensMinusUnstakeFee =
    estimatedTokensForVaultsStaked - Number(earlyUnstakeFee)

  console.log('unstake vaults', {
    expiresAt,
    _expiresAt,
    stakedAt,
    _stakedAt,
    standardPoolRewardsForStake,
    bonusPoolRewardsForStake,
    daysSinceStake,
    daysRemainingInStake,
    totalRewardsLeftToGain,
    earlyUnstakeFee,
    totalRewardsSinceStake,
    estimatedTokensMinusUnstakeFee,
    totalTokensStaked,
    estimatedTokensForVaultsStaked,
  })

  const { width: screenWidth } = useWindowSize()
  const isMobile = screenWidth < 1000
  const [isUnstakeModalOpen, setIsUnstakeModalOpen] = useState(false)

  return (
    <>
      <Box key={`${tierIndex}-${_vaultsStaked}`}>
        {_vaultsStaked > 0 && (
          <Box ac g={8}>
            <Box dc afe jfe mw="32px">
              <Menu button={<DotsButton />}>
                <Box dc>
                  <MenuItem
                    disabled={vaultsUnstakingStatus === 'unstaking'}
                    label={
                      <Box
                        dc
                        g={4}
                        pad={12}
                        onClick={() => setIsUnstakeModalOpen(true)}
                      >
                        <Box ac g={8}>
                          <XCircle size={20} color="white" />
                          <Strong s="S" fw="regular" c="text-primary">
                            Unstake Vaults
                          </Strong>
                        </Box>
                        {/*{!!earlyUnstakeFee && (*/}
                        {/*  <Box padL={28}>*/}
                        {/*    <Span s="S" fw="regular" c="text-error">*/}
                        {/*      /!*{earlyUnstakeFee.toLocaleString()} $ZAP Unstake Fee*!/*/}
                        {/*      Early unstake fee*/}
                        {/*    </Span>*/}
                        {/*  </Box>*/}
                        {/*)}*/}
                      </Box>
                    }
                  />
                  {/*<Divider />
                        <MenuItem
                          label={
                            <Box g={8} ac pad={12}>
                              <XCircle size={20} color="white" />
                              <Strong s="S" fw="regular" c="text-primary">
                                Unstake Vaults
                              </Strong>
                            </Box>
                          }
                        />*/}
                </Box>
              </Menu>
            </Box>
            {/* <VaultsAmount amount={vaultsStaked} /> */}
            <Box dc>
              <P s="L" fw="semibold" c="text-primary">
                {_vaultsStaked} {_vaultsStaked > 1 ? 'Vaults' : 'Vault'}
              </P>
              <P s="S" c="text-secondary">
                {tierMonthsMap[tierIndex]}
              </P>
            </Box>
            <Box dc afe jfe mw="88px">
              {autoRelock && <FolderLock color="white" size={20} />}
            </Box>
            {/* <Box dc afe jfe mw="88px">
            <Strong s="L" fw="semibold" c="text-primary">
              {apy ? `${apy}%` : 'n/a'}
            </Strong>
            <Span s="S" fw="regular" c="text-secondary">
              {multiplier}x
            </Span>
          </Box> */}
            <Box dc afe jfe mw="60px">
              {_expiresAt > new Date() ? (
                <>
                  <Span s="S" fw="regular" c="text-secondary">
                    {daysRemainingInStake}d
                  </Span>
                  {/*<Span s="S" fw="regular" c="text-secondary">*/}
                  {/*  Remaining*/}
                  {/*</Span>*/}
                </>
              ) : (
                <Span s="S" fw="regular" c="text-secondary">
                  Ended
                </Span>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <CheckoutModalOverlay
        isOpen={isUnstakeModalOpen}
        dismiss={() => setIsUnstakeModalOpen(false)}
      >
        <div style={{ width: isMobile ? '340px' : '628px', maxWidth: '100%' }}>
          <Box dc g={32}>
            <PureBackButton onClick={() => setIsUnstakeModalOpen(false)} />

            <H2 s="XL" fw="semibold" c="text-primary">
              Unstake details
            </H2>

            <Box dc g={16}>
              <BorderedBox dc pad={16} g={12}>
                <P s="M" fw="regular" c="text-secondary">
                  Vaults staked
                </P>
                <Divider />
                <Box g={8}>
                  <img
                    src="/vault-icon.svg"
                    alt="zap vault icon"
                    style={{ height: '38px' }}
                  />
                  <Box dc g={2}>
                    <Strong s="L" fw="semibold">
                      {_vaultsStaked.toLocaleString()}{' '}
                      {_vaultsStaked > 1 ? 'Vaults' : 'Vault'} holding{' '}
                      {Math.floor(
                        estimatedTokensForVaultsStaked,
                      ).toLocaleString()}{' '}
                      tokens
                    </Strong>
                    <P s="S" c="text-secondary">
                      {tierMonthsMap[tierIndex]}
                    </P>
                  </Box>
                </Box>
              </BorderedBox>

              <BorderedBox dc pad={16} g={12}>
                <Box dc g={8}>
                  <P s="M" fw="regular" c="text-secondary">
                    Tokens foregoing rewards from unstaking
                  </P>
                  <Box g={8} ac>
                    <img
                      src="/zap-coin.svg"
                      alt="zap coin icon"
                      style={{ height: '24px' }}
                    />
                    <Strong s="L" fw="semibold" c="text-primary">
                      {Math.floor(
                        estimatedTokensForVaultsStaked + totalRewardsLeftToGain,
                      ).toLocaleString()}{' '}
                      tokens
                    </Strong>
                  </Box>
                </Box>
                <Divider />
                <Box dc g={8}>
                  <P s="M" fw="regular" c="text-secondary">
                    Rewards lost due to unstaking
                  </P>
                  <Box g={8} ac>
                    <img
                      src="/zap-coin.svg"
                      alt="zap coin icon"
                      style={{ height: '24px' }}
                    />
                    <Strong s="L" fw="semibold" c="text-error">
                      {'-'}
                      {Math.floor(totalRewardsLeftToGain).toLocaleString()}{' '}
                      tokens
                    </Strong>
                  </Box>
                </Box>
              </BorderedBox>
            </Box>

            <H3 s="L" fw="semibold" c="text-primary">
              You'll receive (after early unstake penalty)
            </H3>

            <BorderedBox dc pad={16}>
              <Box g={8} ac>
                <img src="/zap-coin.svg" alt="zap coin icon" />
                <Strong s="XL" fw="semibold" c="text-brandPrimary">
                  {_vaultsStaked.toLocaleString()}{' '}
                  {_vaultsStaked > 1 ? 'Vaults' : 'Vault'} holding{' '}
                  {Math.floor(estimatedTokensMinusUnstakeFee).toLocaleString()}{' '}
                  tokens
                </Strong>
              </Box>
            </BorderedBox>

            <ThemedButton
              $styleType="primary"
              onClick={() => unstakeVaults(tierIndex)}
              disabled={vaultsUnstakingStatus === 'unstaking'}
            >
              {vaultsUnstakingStatus === 'unstaking' ? (
                <AnimatedLoader size={14} />
              ) : (
                'Unstake'
              )}
            </ThemedButton>
          </Box>
        </div>
      </CheckoutModalOverlay>
    </>
  )
}

export function TokensRow({
  tierIndex,
  tokensStaked,
  previousStake,
  stakedAt,
  expiresAt,
  unstakedAt,
  autoRelock,
}: UserTokenStake) {
  const { width: screenWidth } = useWindowSize()
  const isMobile = screenWidth < 1000

  const {
    getEarlyUnstakeFee,
    unstakeTokens,
    tokensUnstakingStatus,
    totalTokensStaked,
  } = useStakingContext()

  const _tokensStaked = Number(improvedAmountFromWei(tokensStaked))

  const _expiresAt = new Date(Number(expiresAt * 1000n))
  const daysRemainingInStake = daysRemaining(_expiresAt)

  const _stakedAt = new Date(Number(stakedAt * 1000n))
  const daysSinceStake = daysSince(_stakedAt)

  const standardPoolRewardsForStake = getDailyStandardPoolRewardsForStake(
    _tokensStaked,
    getTokenMultiplier(tierIndex),
    totalTokensStaked,
  )

  const totalRewardsLeftToGain =
    standardPoolRewardsForStake * (daysRemainingInStake || 1)

  const [earlyUnstakeFee, setEarlyUnstakeFee] = useState<bigint>(0n)
  useEffect(() => {
    if (!getEarlyUnstakeFee || !_tokensStaked || !stakedAt || !expiresAt) return

    getEarlyUnstakeFee(
      BigInt(_tokensStaked),
      Number(stakedAt),
      Number(expiresAt),
    ).then((fee) => setEarlyUnstakeFee(improvedAmountFromWei(fee)))
  }, [getEarlyUnstakeFee, _tokensStaked, stakedAt, expiresAt])

  const totalRewardsSinceStake =
    standardPoolRewardsForStake * (daysSinceStake || 1)
  const withdrawAmount = BigInt(_tokensStaked) - earlyUnstakeFee

  console.log('unstake tokens', {
    _tokensStaked,
    expiresAt,
    _expiresAt,
    stakedAt,
    _stakedAt,
    standardPoolRewardsForStake,
    daysRemainingInStake,
    totalRewardsLeftToGain,
    earlyUnstakeFee,
    totalRewardsSinceStake,
    withdrawAmount,
    totalTokensStaked,
    daysSinceStake,
  })

  const [isUnstakeModalOpen, setIsUnstakeModalOpen] = useState(false)

  return (
    <>
      <Box key={`${tierIndex}-${tokensStaked}`}>
        {_tokensStaked > 0 && (
          <Box ac g={8}>
            <Box dc afe jfe mw="32px">
              <Menu button={<DotsButton />}>
                <Box dc>
                  <MenuItem
                    disabled={tokensUnstakingStatus === 'unstaking'}
                    label={
                      <Box
                        dc
                        g={4}
                        pad={12}
                        onClick={() => setIsUnstakeModalOpen(true)}
                      >
                        <Box ac g={8}>
                          <XCircle size={20} color="white" />
                          <Strong s="S" fw="regular" c="text-primary">
                            Unstake tokens
                          </Strong>
                        </Box>
                        {/*{!!earlyUnstakeFee && (*/}
                        {/*  <Box padL={28}>*/}
                        {/*    <Span s="S" fw="regular" c="text-error">*/}
                        {/*      /!*{earlyUnstakeFee.toLocaleString()} $ZAP Unstake Fee*!/*/}
                        {/*      Early unstake fee*/}
                        {/*    </Span>*/}
                        {/*  </Box>*/}
                        {/*)}*/}
                      </Box>
                    }
                  />
                  {/*<Divider />
                        <MenuItem
                          label={
                            <Box g={8} ac pad={12}>
                              <XCircle size={20} color="white" />
                              <Strong s="S" fw="regular" c="text-primary">
                                Unstake Vaults
                              </Strong>
                            </Box>
                          }
                        />*/}
                </Box>
              </Menu>
            </Box>
            {/* <VaultsAmount amount={vaultsStaked} /> */}
            <Box dc>
              <P s="L" fw="semibold" c="text-primary">
                {_tokensStaked.toLocaleString()} tokens
              </P>
              <P s="S" c="text-secondary">
                {tierMonthsMap[tierIndex]}
              </P>
            </Box>
            <Box dc afe jfe mw="88px">
              {autoRelock && <FolderLock color="white" size={20} />}
            </Box>
            {/* <Box dc afe jfe mw="88px">
            <Strong s="L" fw="semibold" c="text-primary">
              {apy ? `${apy}%` : 'n/a'}
            </Strong>
            <Span s="S" fw="regular" c="text-secondary">
              {multiplier}x
            </Span>
          </Box> */}
            <Box dc afe jfe mw="60px">
              {_expiresAt > new Date() ? (
                <>
                  <Strong s="S" fw="regular" c="text-secondary">
                    {daysUntil(_expiresAt)}
                  </Strong>
                  {/*<Span s="S" fw="regular" c="text-secondary">*/}
                  {/*  Remaining*/}
                  {/*</Span>*/}
                </>
              ) : (
                <Strong s="L" fw="semibold" c="text-primary">
                  Ended
                </Strong>
              )}
            </Box>
          </Box>
        )}
      </Box>
      <CheckoutModalOverlay
        isOpen={isUnstakeModalOpen}
        dismiss={() => setIsUnstakeModalOpen(false)}
      >
        <div style={{ width: isMobile ? '340px' : '628px', maxWidth: '100%' }}>
          <Box dc g={32}>
            <PureBackButton onClick={() => setIsUnstakeModalOpen(false)} />

            <H2 s="XL" fw="semibold" c="text-primary">
              Unstake details
            </H2>

            <Box dc g={16}>
              <BorderedBox dc pad={16} g={12}>
                <P s="M" fw="regular" c="text-secondary">
                  Tokens staked
                </P>
                <Divider />
                <Box g={8}>
                  <img
                    src="/zap-coin.svg"
                    alt="zap coin icon"
                    style={{ height: '38px' }}
                  />
                  <Box dc g={2}>
                    <Strong s="L" fw="semibold">
                      {_tokensStaked.toLocaleString()} tokens
                    </Strong>
                    <P s="S" c="text-secondary">
                      {tierMonthsMap[tierIndex]}
                    </P>
                  </Box>
                </Box>
              </BorderedBox>

              <BorderedBox dc pad={16} g={12}>
                <P s="M" fw="regular" c="text-secondary">
                  Rewards lost due to unstaking
                </P>
                <Divider />
                <Box g={8} ac>
                  <img
                    src="/zap-coin.svg"
                    alt="zap coin icon"
                    style={{ height: '24px' }}
                  />
                  <Strong s="L" fw="semibold" c="text-error">
                    {'-'}
                    {Math.floor(totalRewardsLeftToGain).toLocaleString()} tokens
                  </Strong>
                </Box>
              </BorderedBox>
            </Box>

            <H3 s="L" fw="semibold" c="text-primary">
              You'll receive (after early unstake penalty)
            </H3>

            <BorderedBox dc pad={16}>
              <Box g={8} ac>
                <img src="/zap-coin.svg" alt="zap coin icon" />
                <Strong s="XL" fw="semibold" c="text-brandPrimary">
                  {Math.floor(Number(withdrawAmount)).toLocaleString()} tokens
                </Strong>
              </Box>
            </BorderedBox>

            <ThemedButton
              $styleType="primary"
              onClick={() => unstakeTokens(tierIndex)}
              disabled={tokensUnstakingStatus === 'unstaking'}
            >
              {tokensUnstakingStatus === 'unstaking' ? (
                <AnimatedLoader size={14} />
              ) : (
                'Unstake'
              )}
            </ThemedButton>
          </Box>
        </div>
      </CheckoutModalOverlay>
    </>
  )
}

const BorderedBox = styled(Box)`
  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`

function ClaimButton({
  isLoading,
  action,
  disabled,
  label,
}: {
  isLoading?: boolean
  action?: () => void
  disabled?: boolean
  label: string
}) {
  return (
    <ThemedButton
      $styleType="primary"
      onClick={action}
      disabled={isLoading || disabled}
    >
      {isLoading ? <AnimatedLoader /> : label}
    </ThemedButton>
  )
}

export function ClaimedVaultsRow() {
  const { stakeInformation, vaultsClaimed } = useStakingContext()
  const { daysRemainingOfVesting } = stakeInformation ?? {}
  const _vaultsClaimed = Number(vaultsClaimed)

  return (
    <Box>
      {_vaultsClaimed > 0 && (
        <Box ac g={8}>
          <Box dc afe jfe mw="32px"></Box>
          {/* <VaultsAmount amount={vaultsStaked} /> */}
          <Box dc>
            <P s="L" fw="semibold" c="text-primary">
              {_vaultsClaimed.toLocaleString()}{' '}
              {_vaultsClaimed > 1 ? 'Vaults' : 'Vault'}
            </P>
          </Box>
          {/* <Box dc afe jfe mw="88px">
            <Strong s="L" fw="semibold" c="text-primary">
              {apy ? `${apy}%` : 'n/a'}
            </Strong>
            <Span s="S" fw="regular" c="text-secondary">
              {multiplier}x
            </Span>
          </Box> */}
          <Box dc afe jfe mw="138px">
            {daysRemainingOfVesting > 0 ? (
              <>
                <Span s="S" fw="regular" c="text-secondary">
                  {daysRemainingOfVesting}
                </Span>
              </>
            ) : daysRemainingOfVesting <= 0 ? (
              <Span s="S" fw="regular" c="text-secondary">
                Ended
              </Span>
            ) : (
              <Span s="S" fw="regular" c="text-secondary">
                n/a
              </Span>
            )}
          </Box>
          {/*<Box dc afe jfe mw="100px">
            {tokensClaimableFromVesting.toLocaleString()}
          </Box>*/}
        </Box>
      )}
    </Box>
  )
}

export const ClaimBox = styled(Box)`
  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-brand, #ff0);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));

  width: calc(50% - 12px);
  flex: unset;
`

function ModalOverlay({
  children,
  isOpen,
  dismiss,
}: {
  children: ReactNode
  isOpen: boolean
  dismiss: () => void
}) {
  return (
    // https://floating-ui.com/docs/dialog#reusable-dialog-component
    // Check the above to understand if you need to extend it
    <Dialog onOpenChange={dismiss} open={isOpen} dismissOnClickOutside={true}>
      <DialogContent>{children}</DialogContent>
    </Dialog>
  )
}

const OuterBox = styled.div`
  margin: auto;
  padding: 32px;
`

const InnerBox = styled(Box)`
  border-radius: var(--units-12, 12px);
  border: 1px solid var(--stroke-secondary, #333);
  background: var(--surface-primary, #080808);
  width: 448px;
  max-width: 100%;
`

export const StyledVaultsBox = styled(Box)`
  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`
